// @flow
import { Button, Divider, Select } from 'antd'
import ModalEmailPreview from 'components/Modal/ModalEmailPreview'
import ModalTextMessagePreview from 'components/Modal/ModalTextMessagePreview'
import { Field, useFormikContext } from 'formik'
import { SettingsEmailMessageInput } from 'pages/Settings/SettingsEmailMessageInput'
import SettingsTextInputWithVariables from 'pages/Settings/SettingsTextInputWithVariables'
import * as React from 'react'
import Ui from 'ui'
import Flex from 'ui/atoms/Flex'
import Space from 'ui/atoms/Space'

import { ButtonGroup } from '@/ui/atoms/ButtonGroup'

import { REMINDER_DAY_OPTIONS } from './settingsTabUtils'

const MAX_TEXT_MESSAGE_LENGTH = 160
const LENGTH_MIN_COLOR = [0, 0, 0, 0.54]
const LENGTH_MAX_COLOR = [200, 30, 30, 1]

const countChars = (text: string) => {
  const modifiedText = text?.replace(/\{\{.*?\}\}/g, '-')
  return modifiedText?.length || 0
}

const clock = (min: number, max: number, ratio: number) =>
  min + (max - min) * ratio

export const SettingsReminder = () => {
  const { values, setFieldValue } = useFormikContext()
  const { sms_body, email_body, reminder_days } = values

  const countTextMessage = countChars(sms_body)

  const [isPreviewEmailVisible, setIsPreviewEmailVisible] =
    React.useState(false)

  const [isPreviewTextVisible, setIsPreviewTextVisible] = React.useState(false)

  const textMessageLengthColor = React.useMemo(() => {
    const ratio = countTextMessage / MAX_TEXT_MESSAGE_LENGTH

    return `rgba(${clock(
      LENGTH_MIN_COLOR[0],
      LENGTH_MAX_COLOR[0],
      ratio
    )}, ${clock(LENGTH_MIN_COLOR[1], LENGTH_MAX_COLOR[1], ratio)}, ${clock(
      LENGTH_MIN_COLOR[2],
      LENGTH_MAX_COLOR[2],
      ratio
    )}, ${clock(LENGTH_MIN_COLOR[3], LENGTH_MAX_COLOR[3], ratio)})`
  }, [countTextMessage])

  return (
    <Space>
      <div className="flex w-full flex-col gap-1.5 pb-3 pt-2">
        <span className="text-base font-bold text-[#374151]">
          Form Reminder
        </span>
        <div className="flex w-full items-center gap-4">
          <div className="flex items-center gap-1">
            <span className="text-base text-gray-600">Send reminder: </span>
            <Select
              className="!w-28"
              onChange={(value) => setFieldValue('reminder_days', value)}
              options={REMINDER_DAY_OPTIONS}
              value={reminder_days}
            />
          </div>
          <ButtonGroup.Root className="w-[fit-content] gap-2 !border-blue-100 bg-[#e2edf8]">
            <ButtonGroup.Item
              className="h-7 !cursor-default select-none px-2.5 text-gray-900 hover:text-gray-900"
              isActive={false}
            >
              After packet/form was sent
            </ButtonGroup.Item>
          </ButtonGroup.Root>
        </div>
      </div>
      <Divider />
      <div>
        <Ui.QuestionTitle htmlFor="email_message">Subject</Ui.QuestionTitle>
        <Field
          component={SettingsTextInputWithVariables}
          name="email_reminder_subject"
          placeholder="Email subject"
        />
      </div>
      <div>
        <Flex align="center" className="relative" justify="between">
          <Ui.QuestionTitle htmlFor="email_message">Message</Ui.QuestionTitle>
          <Button
            className="absolute right-0 top-0 z-[1] h-7 px-1 text-sm"
            onClick={() => setIsPreviewEmailVisible(true)}
            type="link"
          >
            Preview
          </Button>
        </Flex>
        <Field
          component={SettingsEmailMessageInput}
          name="email_body"
          placeholder="Email message"
        />
      </div>
      <div>
        <Flex align="center" className="relative" justify="between">
          <Ui.QuestionTitle htmlFor="text_message">
            Text message
            <span
              className="px-2 text-xs"
              style={{ color: textMessageLengthColor }}
            >
              {countTextMessage}/{MAX_TEXT_MESSAGE_LENGTH}
            </span>
          </Ui.QuestionTitle>
          <Button
            className="absolute right-0 top-0 z-[1] h-7 px-1 text-sm"
            onClick={() => setIsPreviewTextVisible(true)}
            type="link"
          >
            Preview
          </Button>
        </Flex>
        <Field
          multiline
          component={SettingsTextInputWithVariables}
          maxLength={160}
          name="sms_body"
          placeholder="Text message"
        />
      </div>
      <ModalEmailPreview
        canDismiss
        isVisible={isPreviewEmailVisible}
        message={email_body}
        onClose={() => setIsPreviewEmailVisible(false)}
      />
      <ModalTextMessagePreview
        canDismiss
        isVisible={isPreviewTextVisible}
        message={sms_body}
        onClose={() => setIsPreviewTextVisible(false)}
      />
    </Space>
  )
}
